/**
 * Tenants list — top of the cost drill-down.
 *
 * One row per tenant that emitted at least one `circuitry.usage` event in
 * the selected window. Sorted by total cost descending. Clicking a row
 * navigates to the per-product breakdown for that tenant, preserving the
 * window via query-string.
 */
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import CostWindowSelector from '@/components/tenants/CostWindowSelector';
import { useTenantCosts } from '@/hooks/useTenantCosts';
import type { CostWindowParams } from '@/api/tenants';

function formatUsd(s: string): string {
  const n = Number(s);
  if (Number.isNaN(n)) return '$0.00';
  if (n >= 100) return `$${n.toFixed(2)}`;
  if (n >= 1) return `$${n.toFixed(3)}`;
  return `$${n.toFixed(4)}`;
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
}

function windowToQuery(w: CostWindowParams): string {
  const p = new URLSearchParams();
  if (w.period) p.set('period', w.period);
  if (w.start) p.set('start', w.start);
  if (w.end) p.set('end', w.end);
  return p.toString();
}

export default function Tenants() {
  const [window, setWindow] = useState<CostWindowParams>({ period: '30d' });
  const [search, setSearch] = useState('');
  const { data, isLoading, error } = useTenantCosts(window);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search.trim()) return data.rows;
    const q = search.trim().toLowerCase();
    return data.rows.filter((r) => r.tenant_id.toLowerCase().includes(q));
  }, [data, search]);

  const totalCost = useMemo(
    () => filtered.reduce((sum, r) => sum + Number(r.total_cost_usd), 0),
    [filtered],
  );

  return (
    <div className="space-y-6 animate-fade-in">
      <header>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Tenants</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Per-tenant cost attribution. Click a tenant to drill into products → services.
        </p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <CostWindowSelector value={window} onChange={setWindow} />
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            placeholder="Filter tenants…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64 rounded-lg border border-slate-200/60 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-700 shadow-sm placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:placeholder:text-slate-500"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">
          Failed to load tenant costs: {(error as Error).message}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200/60 bg-white shadow-card overflow-hidden dark:border-slate-800/80 dark:bg-slate-900">
        <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-700">
          <thead className="bg-surface-50 dark:bg-slate-800/50">
            <tr>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Tenant
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Cost
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Events
              </th>
              <th className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Products
              </th>
              <th className="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                Last Activity
              </th>
              <th className="w-8 px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white dark:divide-slate-800 dark:bg-slate-900">
            {isLoading && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Loading…
                </td>
              </tr>
            )}
            {!isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No tenants in this window. Did the seed script run?
                </td>
              </tr>
            )}
            {filtered.map((row) => (
              <tr
                key={row.tenant_id}
                className="hover:bg-surface-50 dark:hover:bg-slate-800/30 transition-colors"
              >
                <td className="whitespace-nowrap px-4 py-3 font-mono text-sm text-slate-900 dark:text-slate-100">
                  {row.tenant_id}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-slate-900 dark:text-slate-100">
                  {formatUsd(row.total_cost_usd)}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {row.event_count.toLocaleString()}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-right tabular-nums text-sm text-slate-600 dark:text-slate-300">
                  {row.distinct_products}
                </td>
                <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                  {formatTime(row.last_event_at)}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/tenants/${encodeURIComponent(row.tenant_id)}?${windowToQuery(window)}`}
                    className="inline-flex items-center text-brand-600 hover:text-brand-700 dark:text-brand-400"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
          {filtered.length > 0 && (
            <tfoot className="bg-surface-50 dark:bg-slate-800/50">
              <tr>
                <td className="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  Total
                </td>
                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-slate-900 dark:text-slate-100">
                  ${totalCost.toFixed(2)}
                </td>
                <td colSpan={4}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
